<template>
  <div class="org-call-log-notify">
    <article
      class="org-notify-channel org-notify-channel--email"
      :class="{ 'org-notify-channel--active': sendCallLogsByEmail }"
    >
      <button
        type="button"
        class="org-notify-channel__head"
        :disabled="disabled"
        @click="onSendEmailChange(!sendCallLogsByEmail)"
      >
        <span class="org-notify-channel__icon org-notify-channel__icon--email" aria-hidden="true">
          <v-icon size="20">mdi-email-sync-outline</v-icon>
        </span>
        <div class="org-notify-channel__intro">
          <h3 class="org-notify-channel__title">Email call logs</h3>
          <p class="org-notify-channel__desc">Send call summaries to your organisation contact email</p>
        </div>
        <span
          class="org-notify-status-pill"
          :class="sendCallLogsByEmail ? 'org-notify-status-pill--on' : 'org-notify-status-pill--off'"
        >
          <v-icon size="14">{{ sendCallLogsByEmail ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}</v-icon>
          {{ sendCallLogsByEmail ? 'Enabled' : 'Disabled' }}
        </span>
      </button>

      <div v-if="sendCallLogsByEmail" class="org-notify-channel__body">
        <p class="org-notify-recipients__lead">
          Optional — add extra email addresses to receive copies of call logs.
        </p>
        <div class="org-notify-recipients">
          <AgentItemsTable
            :model-value="emailAddressRows"
            title="Additional emails"
            add-label="Add email"
            empty-text="No extra emails added. Use Add email if you want copies sent elsewhere."
            value-column-label="Email"
            value-placeholder="name@company.com"
            value-input-type="email"
            prepend-value-icon="mdi-email-outline"
            :value-rules="emailFieldRules"
            :outlined-values="true"
            :show-parameter-name="false"
            :show-required="false"
            :min-rows="0"
            :disabled="disabled"
            @update:model-value="$emit('update:emailAddressRows', $event)"
          />
        </div>
      </div>
    </article>

    <article
      class="org-notify-channel org-notify-channel--whatsapp"
      :class="{ 'org-notify-channel--active': sendCallLogsByWhatsapp }"
    >
      <button
        type="button"
        class="org-notify-channel__head"
        :disabled="disabled"
        @click="onSendWhatsappChange(!sendCallLogsByWhatsapp)"
      >
        <span class="org-notify-channel__icon org-notify-channel__icon--whatsapp" aria-hidden="true">
          <v-icon size="20">mdi-whatsapp</v-icon>
        </span>
        <div class="org-notify-channel__intro">
          <h3 class="org-notify-channel__title">WhatsApp call logs</h3>
          <p class="org-notify-channel__desc">Deliver summaries on WhatsApp · 1 credit per message</p>
        </div>
        <span
          class="org-notify-status-pill"
          :class="sendCallLogsByWhatsapp ? 'org-notify-status-pill--on' : 'org-notify-status-pill--off'"
        >
          <v-icon size="14">{{ sendCallLogsByWhatsapp ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}</v-icon>
          {{ sendCallLogsByWhatsapp ? 'Enabled' : 'Disabled' }}
        </span>
      </button>

      <div v-if="sendCallLogsByWhatsapp" class="org-notify-channel__body">
        <p class="org-notify-recipients__lead">
          Optional — add extra mobile numbers to receive WhatsApp copies.
        </p>
        <div class="org-notify-recipients">
          <AgentItemsTable
            :model-value="whatsappNumberRows"
            title="Additional numbers"
            add-label="Add number"
            empty-text="No extra numbers added. Use Add number if you want WhatsApp copies elsewhere."
            value-column-label="Mobile"
            value-placeholder="10-digit number"
            value-input-type="tel"
            prepend-value-icon="mdi-phone-outline"
            :value-rules="mobileFieldRules"
            :outlined-values="true"
            :show-parameter-name="false"
            :show-required="false"
            :min-rows="0"
            :disabled="disabled"
            @update:model-value="$emit('update:whatsappNumberRows', $event)"
          />
        </div>
      </div>
    </article>

    <OrgOptionTile
      :model-value="restrictIncomingCalls"
      class="org-call-log-notify__item--full"
      label="Restrict incoming calls"
      hint="Only allow callers already in your Callers list"
      icon="mdi-shield-lock-outline"
      :disabled="disabled"
      @update:model-value="$emit('update:restrictIncomingCalls', $event)"
    />
  </div>
</template>

<script>
import OrgOptionTile from '@/components/Organizations/ui/OrgOptionTile.vue'
import AgentItemsTable from '@/components/Organizations/Tabs/AgentItemsTable.vue'
import { isValidEmail, isValidMobile10 } from '@/utils/agentConfiguration'

export default {
  name: 'OrganizationCallLogNotificationFields',
  components: { OrgOptionTile, AgentItemsTable },
  props: {
    sendCallLogsByEmail: { type: Boolean, default: true },
    emailAddressRows: { type: Array, default: () => [] },
    sendCallLogsByWhatsapp: { type: Boolean, default: false },
    whatsappNumberRows: { type: Array, default: () => [] },
    restrictIncomingCalls: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: [
    'update:sendCallLogsByEmail',
    'update:emailAddressRows',
    'update:sendCallLogsByWhatsapp',
    'update:whatsappNumberRows',
    'update:restrictIncomingCalls',
  ],
  computed: {
    emailFieldRules() {
      return [
        (v) => !String(v ?? '').trim() || isValidEmail(v) || 'Enter a valid email',
      ]
    },
    mobileFieldRules() {
      return [
        (v) => !String(v ?? '').trim() || isValidMobile10(v) || 'Enter a valid 10-digit number',
      ]
    },
  },
  methods: {
    onSendEmailChange(value) {
      this.$emit('update:sendCallLogsByEmail', value)
      if (!value) {
        this.$emit('update:emailAddressRows', [])
      }
    },
    onSendWhatsappChange(value) {
      this.$emit('update:sendCallLogsByWhatsapp', value)
      if (!value) {
        this.$emit('update:whatsappNumberRows', [])
      }
    },
  },
}
</script>
